import type { ReactNode } from "react";

import { cn } from "./cn";

type PageHeaderProps = Readonly<{
  /** The `h1` of the page. */
  title: string;
  description?: ReactNode;
  /** The buttons that belong to the whole page (Create, Refresh). They sit on the right and wrap under the title on a narrow screen. */
  actions?: ReactNode;
  /** A line above the title (for example a small label). */
  eyebrow?: ReactNode;
  /** The slot for `Breadcrumbs` when a page shows its own trail. The shell already shows the trail of the route in its top bar. */
  breadcrumbs?: ReactNode;
  /** The `id` of the `h1`, for a page section that names itself with `aria-labelledby` (the pages use `page-title`). */
  titleId?: string;
  /** Extra classes of the `h1`, for a page whose title is larger than the usual one (a payment shows its amount). */
  titleClassName?: string;
  className?: string;
}>;

/** The top of a page: an optional trail, the `h1`, a sentence under it and the page actions. */
export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
  breadcrumbs,
  titleId,
  titleClassName,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn("grid gap-3", className)}>
      {breadcrumbs}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="grid min-w-0 gap-1">
          {eyebrow ? (
            <p className="m-0 text-meta font-semibold tracking-[0.06em] text-muted-foreground uppercase">
              {eyebrow}
            </p>
          ) : null}
          <h1
            id={titleId}
            className={cn(
              "m-0 font-serif text-3xl leading-9 font-medium tracking-[-0.015em] text-balance sm:text-[40px] sm:leading-[44px]",
              titleClassName,
            )}
          >
            {title}
          </h1>
          {description ? (
            <p className="m-0 max-w-prose text-sm leading-6 text-muted-foreground sm:text-base">
              {description}
            </p>
          ) : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </header>
  );
}
